import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import Icon from '../Icon';
import LoadingIndicator from '../LoadingIndicator';
import { DEFAULT_TOOL_TIP_DELAY } from '../Tooltip/Tooltip.constants';
import { browserIsFirefox } from '../utils/browserId';
import { buttonInteractiveStyles, buttonSizeStyles, buttonStyles, buttonVariantStyles } from './Button.styles';
import type React from 'react';
import type { ComponentProps } from 'react';
import type { QTipDataAttributes } from '../Tooltip/QTip.types';
import type { ButtonProps } from './Button.types';

type ButtonComponentProps = ButtonProps &
  React.HtmlHTMLAttributes<HTMLButtonElement> &
  React.RefAttributes<HTMLButtonElement>;
type LoadingIndicatorSize = ComponentProps<typeof LoadingIndicator>['size'];

/**
 * All-in-one Button:
 *  - use "variant" to achieve the desired style
 *  - include tooltips and/or icons
 */
const Button = ({
  onClick,
  label,
  variant = 'outline',
  type = 'button',
  size = 'sm',
  disabled,
  inProgress = false,
  extraClassNames,
  xstyle,
  style,
  id,
  testId,
  stopPropagation = true,
  tooltip,
  tooltipOptions,
  iconStyle = 'text',
  icon,
  iconColor,
  iconXstyle,
  iconPosition = 'left',
  iconPrefix,
  preventBlur = false,
  isHtmlTooltip = false,
  tooltipTestId,
  'aria-label': ariaLabel,
  ref,
  ...rest
}: ButtonComponentProps) => {
  const loadingIndicatorSizes: Record<typeof size, LoadingIndicatorSize> = {
    xs: 'xs',
    sm: 'sm',
    lg: 'md',
  };
  let tooltipData: QTipDataAttributes | undefined;
  const styleProps = stylex.props(
    buttonStyles.base,
    buttonVariantStyles[variant],
    buttonSizeStyles[size],
    !disabled && buttonInteractiveStyles[variant],
    disabled && buttonStyles.disabled,
    disabled && !tooltip && buttonStyles.nativeDisabled,
    xstyle,
  );

  if (tooltip) {
    tooltipData = {
      'data-qtip-text': tooltip,
      'data-qtip-placement': tooltipOptions?.position,
      'data-qtip-is-html': isHtmlTooltip,
      'data-qtip-testid': tooltipTestId,
      'data-qtip-delay': tooltipOptions?.delay ?? DEFAULT_TOOL_TIP_DELAY,
    };
  }

  const resolvedAriaLabel = ariaLabel ?? (!label && icon ? tooltip : undefined);

  const iconStyles = label
    ? [iconPosition === 'left' ? buttonStyles.iconLeft : buttonStyles.iconRight, buttonStyles.iconColor]
    : undefined;
  const iconTestId = id ? `${id}_spinner` : undefined;
  const loadingIndicatorTestId = iconTestId ?? (testId ? `${testId}_spinner` : undefined);
  const isLoading = inProgress === true || inProgress === 'loading';
  const isStreaming = inProgress === 'streaming';

  const iconElement = isLoading ? (
    <LoadingIndicator
      size={loadingIndicatorSizes[size]}
      color={iconStyle === 'color' && iconColor ? iconColor : 'currentColor'}
      xstyle={label ? (iconPosition === 'left' ? buttonStyles.iconLeft : buttonStyles.iconRight) : undefined}
      testId={loadingIndicatorTestId}
    />
  ) : (
    icon && (
      <Icon
        icon={icon}
        iconPrefix={iconPrefix}
        type={iconStyle}
        color={iconColor}
        xstyle={[iconStyles, isStreaming ? buttonStyles.streamingIcon : null, iconXstyle]}
        testId={iconTestId}
      />
    )
  );

  return (
    <button
      {...rest}
      ref={ref}
      id={id}
      {...tooltipData}
      disabled={disabled && !tooltip}
      aria-disabled={disabled}
      aria-label={resolvedAriaLabel}
      data-testid={testId}
      data-variant={variant}
      tabIndex={disabled ? -1 : undefined}
      type={type === 'link' || (type === 'submit' && browserIsFirefox) ? 'button' : type}
      onClick={(e: React.MouseEvent) => {
        stopPropagation && e.stopPropagation();
        if (!disabled) {
          onClick?.(e);
        }
      }}
      onMouseDown={(e) => {
        if (preventBlur) {
          e.preventDefault();
        }
      }}
      className={clsx(styleProps.className, extraClassNames)}
      style={{ ...styleProps.style, ...style }}>
      {iconPosition === 'left' && iconElement}
      {label}
      {iconPosition === 'right' && iconElement}
    </button>
  );
};
export default Button;
